/**
 * Size guide CM/IN toggle.
 * Page images (order):
 *   2 → CM, IN
 *   4 → Desktop CM, Desktop IN, Mobile CM, Mobile IN
 * Unisex size-chart page ([data-cw-size-guide-stack]), 8 images:
 *   first 4 Men's, next 4 Women's, same order. CM/IN panels stack them.
 *
 * For sharp mobile retina, export mobile charts ≥1500px wide
 * (750px masters look soft on 2x/3x phones).
 */
const CW_SG_MOBILE_MQ = '(max-width: 749px)';
const CW_SG_MOBILE_WIDTHS = [375, 750, 1125, 1500, 2250];
const CW_SG_DESKTOP_WIDTHS = [540, 1081, 1622, 2162];
const CW_SG_DESKTOP_SIZES = '(min-width: 1920px) 54rem, (min-width: 1440px) 48rem, 42rem';
const CW_SG_MOBILE_SIZES = '100vw';

function setSizeGuideUnit(root, unit) {
  if (!root || (unit !== 'cm' && unit !== 'in')) return;

  root.querySelectorAll('[data-cw-unit]').forEach((btn) => {
    const active = btn.getAttribute('data-cw-unit') === unit;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
  });

  root.querySelectorAll('[data-cw-unit-panel]').forEach((panel) => {
    panel.hidden = panel.getAttribute('data-cw-unit-panel') !== unit;
  });
}

/** Full-size URL: linked original > largest srcset > src. */
function rawImgSrc(img) {
  const link = img.closest('a[href]');
  if (link) {
    const href = (link.getAttribute('href') || '').trim();
    if (href && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(href)) return href;
  }

  const srcset = img.getAttribute('srcset');
  if (srcset) {
    let best = '';
    let bestW = -1;
    srcset.split(',').forEach((part) => {
      const bits = part.trim().split(/\s+/);
      const url = bits[0];
      if (!url) return;
      const desc = bits[1] || '';
      const w = desc.endsWith('w') ? parseInt(desc, 10) : 0;
      if (w >= bestW) {
        best = url;
        bestW = w;
      }
    });
    if (best) return best;
  }

  return (img.getAttribute('src') || img.currentSrc || '').trim();
}

/** Original file URL — strip size suffix / width (never ask CDN to upscale). */
function masterUrl(url) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.pathname = u.pathname.replace(/_(?:\d+x\d+|\d+x)(?=\.[^./]+$)/, '');
    ['width', 'height', 'crop', 'pad_color'].forEach((k) => u.searchParams.delete(k));
    return u.href;
  } catch {
    return url;
  }
}

function shopifyImageUrl(url, width) {
  if (!url) return url;
  try {
    const u = new URL(masterUrl(url), window.location.origin);
    if (width > 0) u.searchParams.set('width', String(width));
    return u.href;
  } catch {
    return url;
  }
}

function srcsetFor(url, widths, maxWidth) {
  const list = widths.filter((w) => w < maxWidth);
  list.push(maxWidth);
  return [...new Set(list)]
    .sort((a, b) => a - b)
    .map((w) => {
      const src = w >= maxWidth ? masterUrl(url) : shopifyImageUrl(url, w);
      return `${src} ${w}w`;
    })
    .join(', ');
}

/**
 * @param {HTMLImageElement | HTMLSourceElement} target
 */
function bindSrcset(target, master, widths, sizes, fallbackMax) {
  const apply = (maxWidth) => {
    target.srcset = srcsetFor(master, widths, maxWidth);
    target.sizes = sizes;
    if (target instanceof HTMLImageElement) {
      target.src = masterUrl(master);
    }
  };

  apply(fallbackMax);

  const probe = new Image();
  probe.onload = () => apply(probe.naturalWidth || fallbackMax);
  probe.onerror = () => apply(fallbackMax);
  probe.src = masterUrl(master);
}

function createChartMedia(desktopImg, mobileImg, eager) {
  const desktopMaster = masterUrl(rawImgSrc(desktopImg));
  const alt = desktopImg.getAttribute('alt') || '';

  const img = document.createElement('img');
  img.className = 'cw-size-guide__image';
  img.alt = alt;
  img.decoding = 'async';
  img.loading = eager ? 'eager' : 'lazy';
  if (eager) img.fetchPriority = 'high';

  const mobileSrc = mobileImg ? rawImgSrc(mobileImg) : '';
  const mobileMaster = mobileSrc ? masterUrl(mobileSrc) : '';

  if (mobileMaster) {
    const picture = document.createElement('picture');
    const source = document.createElement('source');
    source.media = CW_SG_MOBILE_MQ;
    bindSrcset(source, mobileMaster, CW_SG_MOBILE_WIDTHS, CW_SG_MOBILE_SIZES, 750);
    picture.appendChild(source);

    bindSrcset(img, desktopMaster, CW_SG_DESKTOP_WIDTHS, CW_SG_DESKTOP_SIZES, 1081);
    picture.appendChild(img);
    return picture;
  }

  bindSrcset(
    img,
    desktopMaster,
    CW_SG_DESKTOP_WIDTHS,
    `(max-width: 749px) 100vw, ${CW_SG_DESKTOP_SIZES}`,
    1081
  );
  return img;
}

function buildUnitPanel(unit) {
  const panel = document.createElement('div');
  panel.className = 'cw-size-guide__panel';
  panel.setAttribute('data-cw-unit-panel', unit);
  return panel;
}

function buildUnitMedia(unit, desktopImg, mobileImg) {
  const panel = buildUnitPanel(unit);
  panel.appendChild(createChartMedia(desktopImg, mobileImg, unit === 'cm'));
  return panel;
}

/** Each group is [desktop CM, desktop IN, mobile CM, mobile IN]. */
function buildStackedUnit(unit, groups) {
  const panel = buildUnitPanel(unit);
  const desktopIndex = unit === 'cm' ? 0 : 1;
  const mobileIndex = unit === 'cm' ? 2 : 3;

  groups.forEach((group, index) => {
    const chart = document.createElement('div');
    chart.className = 'cw-size-guide__chart';
    chart.appendChild(
      createChartMedia(group[desktopIndex], group[mobileIndex], unit === 'cm' && index === 0)
    );
    panel.appendChild(chart);
  });

  return panel;
}

/**
 * Init (or re-init) from page HTML.
 * Theme morph / view-transitions can restore raw source while leaving
 * data-cw-size-guide-ready="true" — that skips transform and shows all 4 charts.
 * Always re-process when [data-cw-size-guide-source] is present.
 */
function initSizeGuideFromPage(root) {
  if (!root || root.dataset.cwSizeGuideBusy === 'true') return;

  const sourceEl = root.querySelector('[data-cw-size-guide-source]');
  if (!sourceEl) {
    if (root.querySelector('[data-cw-unit-panel]')) {
      setSizeGuideUnit(root, root.getAttribute('data-cw-default-unit') || 'cm');
      root.dataset.cwSizeGuideReady = 'true';
    }
    return;
  }

  root.dataset.cwSizeGuideBusy = 'true';
  delete root.dataset.cwSizeGuideReady;
  root.querySelectorAll('[data-cw-unit-panel]').forEach((panel) => panel.remove());

  const images = Array.from(sourceEl.querySelectorAll('img')).filter((img) => rawImgSrc(img));

  if (images.length < 2) {
    const toggle = root.querySelector('.cw-size-guide__toggle');
    if (toggle) toggle.hidden = true;
    delete root.dataset.cwSizeGuideBusy;
    root.dataset.cwSizeGuideReady = 'true';
    return;
  }

  const stack = root.hasAttribute('data-cw-size-guide-stack') && images.length >= 8;
  let panelCm;
  let panelIn;

  if (stack) {
    const groups = [];
    const count = images.length - (images.length % 4);
    for (let i = 0; i < count; i += 4) groups.push(images.slice(i, i + 4));
    panelCm = buildStackedUnit('cm', groups);
    panelIn = buildStackedUnit('in', groups);
  } else {
    const hasMobile = images.length >= 4;
    panelCm = buildUnitMedia('cm', images[0], hasMobile ? images[2] : null);
    panelIn = buildUnitMedia('in', images[1], hasMobile ? images[3] : null);
  }

  sourceEl.replaceWith(panelCm);
  panelCm.after(panelIn);
  setSizeGuideUnit(root, root.getAttribute('data-cw-default-unit') || 'cm');
  delete root.dataset.cwSizeGuideBusy;
  root.dataset.cwSizeGuideReady = 'true';
}

function initSizeGuides(scope) {
  (scope || document).querySelectorAll('[data-cw-size-guide-page]').forEach(initSizeGuideFromPage);
}

window.cwInitSizeGuides = initSizeGuides;

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-cw-unit]');
  if (!btn) return;
  const root = btn.closest('[data-cw-size-guide]');
  if (root) setSizeGuideUnit(root, btn.getAttribute('data-cw-unit'));
});

const boot = () => initSizeGuides(document);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      initSizeGuides(event.target);
    }
  },
  true
);

document.addEventListener('shopify:section:load', (event) => {
  initSizeGuides(event.target instanceof Element ? event.target : document);
});

window.addEventListener('pageshow', () => initSizeGuides(document));